import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useHistory} from 'react-router-dom'
import styled from 'styled-components'

import {useActionApi, useApi, usePersistedApi, usePersistedState} from 'hooks'
import Api, {CandidateStats} from 'api'
import {entities} from 'reduxStore'

import {BackendJob} from 'components/EditJob'
import {Application, ApplicationStatus} from 'components/JobBoardDetails'
import {PurchaseTokensFlow} from 'components/PurchaseTokensFlow';
import CandidateView, {CandidateDetails, InterviewStatus} from 'components/CandidateView'
import FixedLoader from 'components/FixedLoader'
import FixedError from 'components/FixedError'
import Popup from 'components/Popup'
import {TopBarRedesigned} from "components/TopBarRedesigned";
import {ApplicationActions} from "components/CandidateView/ApplicationActions";
import {NoShowConfirmation} from "components/CandidateView/NoShowConfirmation";
import {ShowContactsConfirmation} from "components/CandidateView/ShowContactsConfirmation";
import {CandidateScoreInfo} from "components/CandidateView/CandidateScoreInfo";
import {RequestResumeAction} from "components/CandidateView/RequestResumeAction";
import {ResumePurchaseFail} from "components/CandidateView/ResumePurchaseFail";
import {ShowContactsAction} from "components/CandidateView/ShowContactsAction";
import {UserAccountProps} from "utils/types";

declare let window: any
declare let cordova: any

interface RecentlyViewedResult {
    recentlyViewed: number;
}

const PageWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  background-color: #fff;
  overflow: auto;
  position: relative;
  min-height: 100vh;
`;

export default function CandidatePage() {
    const history = useHistory()
    const {job}: { job: BackendJob } = history.location.state as any

    // can be updated with a additional info in case user makes a purchase
    const [application, setApplication] = usePersistedState<Application>({
        entityName: entities.dentistCandidateApplication
    })
    const [applicationActionsPopupOpened, setApplicationActionsPopupOpened] = useState(false)
    const [requestResumePopupOpened, setRequestResumePopupOpened] = useState(false)
    const [noShowPopupOpened, setNoShowPopupOpened] = useState(false)
    const [resumePurchaseFailPopupOpened, setResumePurchaseFailPopupOpened] = useState(false)
    const [showContactsConfirmationPopupOpened, setShowContactsConfirmationPopupOpened] = useState(false);
    const [showContactsPopupOpened, setShowContactsPopupOpened] = useState(false);
    const [scoreInfoPopupOpened, setScoreInfoPopupOpened] = useState(false)
    const [tokensPurchaseOpened, setTokensPurchaseOpened] = useState(false)
    const [recentlyViewedResume, setRecentlyViewedResume] = useState(0)
    const [
        candidateStats,
        candidateStatsLoading,
        candidateStatsError,
        getHygienistStatsById
    ] = useApi({
        apiMethod: Api.getHygienistStatsById,
        initialValue: {} as CandidateStats
    })

    const onViewResume = () => {
        history.push({ pathname: "candidateResume" });
    }

    const [dentistUser, dentistUserLoading, dentistUserError, getDentistUser] = usePersistedApi({
        apiMethod: Api.getUser,
        entityName: entities.user,
        initialValue: {} as UserAccountProps,
    })
    const [billingLocations, locationsLoading, locationsError, getBillingLocations] = usePersistedApi({
        apiMethod: Api.getLocations,
        entityName: entities.dentistBillingLocations,
        initialValue: [],
    })
    const [allowSeeContacts, allowSeeContactsLoading, allowSeeContactsError, allowShowInfo] = useApi({
        apiMethod: Api.allowShowInfo,
        initialValue: {allow: application.showContacts || false},
        onSuccess: (data) => {
            if (data && data.allow) {
                setShowContactsPopupOpened(false);
                setShowContactsConfirmationPopupOpened(true);
            }
        },
        onError: () => {
            setShowContactsPopupOpened(false);
        }
    })
    const [purchaseLoading, purchaseError, purchaseResume] = useActionApi({
        apiMethod: Api.purchaseResume,
        onSuccess: (updatedApplication: Application) => {
            const newApplication = {
                ...application,
                ...updatedApplication
            };

            setApplication(newApplication);
            newApplication.hireResumeUrl && onViewResume();

            if (!newApplication.showContacts) {
                setShowContactsPopupOpened(true);
            }
        },
        onError: () => {
            setResumePurchaseFailPopupOpened(true)
        }
    })

    useEffect(() => {
        getDentistUser({})
        getBillingLocations({})
    }, [getDentistUser, getBillingLocations])

    const [getAllApplicationsLoading, getAllApplicationsError, getAllApplications] = useActionApi({
        apiMethod: Api.getApplicationsForHireJob,
        onSuccess: (applications: Application[]) => {
            const updatedApplication = applications.find((app: Application) => app.applicationId === application.applicationId);
            
            if (updatedApplication) {
                setApplication(updatedApplication);
            }
        },
        onError: () => {
            console.error('Error fetching applications');
        }
    });

    useEffect(() => {
        if (job.id !== undefined) {
            getAllApplications(job.id);
        }
    }, []);

    const [
        applicationLoading,
        applicationError,
        updateApplication
    ] = useActionApi({
        apiMethod: Api.updateApplication,
        onSuccess: useCallback(
            (updatedData: Application) => {
                setApplication({
                    ...application,
                    ...updatedData
                })
            },
            [setApplication, application]
        )
    })

    const onAllowShowInfo = (newStatus: boolean) => {
        allowShowInfo({ApplicationId: application.applicationId, allow: newStatus})
    }

    const updateStatus = async (status: ApplicationStatus) => {
        await updateApplication({
            applicationId: application.applicationId,
            newData: {
                status,
            }
        })
    }

    const onShortlistCandidate = async () => {
        await updateStatus(ApplicationStatus.shortlisted);
    }

    const onDeclineCandidate = async () => {
        await updateStatus(ApplicationStatus.declined);
    }

    useEffect(() => {
        getHygienistStatsById({ 
            hygienistId: application.hygienistId, 
            applicationId: application.applicationId 
        })
        if (application.status === 0) {
            updateStatus(1)
        }
    }, [getHygienistStatsById, application.hygienistId, application.status])

    const candidateDetails = useMemo(() => {
        const workHistory =
            (candidateStats.office_completed_num === 0 || candidateStats.office_completed_num === null) && candidateStats.lastScore === 0
                ? 'This candidate has not previously worked at your office'
                : candidateStats.office_completed_num > 0 && candidateStats.lastScore === 0
                    ? 'This candidate has worked at your office previously, but you didn\'t rate them'
                    : candidateStats.lastScore >= 3
                        ? 'This candidate has worked at your office previously and you indicated you liked them.'
                        : 'This candidate has worked at your office previously and you indicated were not happy with them.'

        const interviewReliability =
            candidateStats.missedInterviewCount === 1
                ? `This candidate has missed ${candidateStats.missedInterviewCount} scheduled interview.`
                : candidateStats.missedInterviewCount > 0
                    ? `This candidate has missed ${candidateStats.missedInterviewCount} scheduled interviews.`
                    : 'This candidate has never missed a scheduled interview.'

        return Number.isFinite(candidateStats.hygienistScore)
            ? // append additional info only after stats loads
            {
                score: candidateStats.hygienistScore,
                reviewsCount: candidateStats.ratings_num,
                workHistory,
                interviewReliability
            } as CandidateDetails
            : undefined
    }, [candidateStats])

    const onSetInterviewStatus = async (newStatus: InterviewStatus) => {
        await updateApplication({
            applicationId: application.applicationId,
            newData: {
                interviewStatus: newStatus
            }
        })
        getHygienistStatsById({ hygienistId: application.hygienistId, applicationId: application.applicationId })
        onApplicationActionsPopupClose()
    }

    const onApplicationActionsPopupClose = () => {
        setApplicationActionsPopupOpened(false);
    }

    const onNoShowButtonClick = () => {
        onApplicationActionsPopupClose()
        setNoShowPopupOpened(true)
    };

    const onCloseNoShowPopup = () => {
        setNoShowPopupOpened(false);
    }

    const onConfirmNoShowPopup = async () => {
        await onSetInterviewStatus(InterviewStatus.noshow);
        onCloseNoShowPopup();
    }

    const onConfirmResumePurchaseFailPopup = () => {
        onCloseResumePurchaseFailPopup();
        setTokensPurchaseOpened(true);
    }

    const onCloseScoreInfoPopup = () => {
        setScoreInfoPopupOpened(false);
    }

    const onLinkCandidateScoreInfoPopup = () => {
        window.Cordova
            ? cordova.InAppBrowser.open(
                'https://support.tempstars.com/hc/en-us/articles/360047266133-Recommendation-Score',
                '_system',
                'location=yes'
            )
            : window.open(
                'https://support.tempstars.com/hc/en-us/articles/360047266133-Recommendation-Score',
                '_blank',
                'location=yes'
            )
    }

    const onCloseShowContactsConfirmationPopup = () => {
        setShowContactsConfirmationPopupOpened(false);
    }

    const onCloseShowContactsPopup = () => {
        setShowContactsPopupOpened(false);
    }

    const onConfirmShowContactsPopup = () => {
        onAllowShowInfo(true);
    }

    const onConfirmResumeActionPopup = async () => {
        onCloseRequestResumePopup();
        await purchaseResume(application.applicationId)
    }

    const onCloseRequestResumePopup = () => {
        setRequestResumePopupOpened(false);
    }

    const onCloseTokensPurchasePopup = () => {
        setTokensPurchaseOpened(false)
    }

    const onSuccessTokensPurchasePopup = async () => {
        onCloseTokensPurchasePopup();
        getDentistUser({})
        await purchaseResume(application.applicationId)
    }

    const onCloseResumePurchaseFailPopup = () => {
        setRequestResumePopupOpened(false)
    }

    const [recentlyViewedResponseLoading, recentlyViewedResponseError, recentlyViewedResponse] = useActionApi({
        apiMethod: Api.checkRecentlyViewed,
        onSuccess: (result: RecentlyViewedResult) => {
            const { recentlyViewed } = result 
            setRecentlyViewedResume(recentlyViewed);
        },
        onError: () => {
            setRecentlyViewedResume(0);
        }
    })

    const onRequestResume = async () => {
        await recentlyViewedResponse(application.applicationId)
        .then(()=> {
            setRequestResumePopupOpened(true);
        })
    }

    return (
        <PageWrapper>
            <TopBarRedesigned
                title="Resumé"
                onBack={() => {
                    history.goBack()
                }}
            />
            <CandidateView
                application={application}
                onScoreInfoButtonClick={() => {
                    setScoreInfoPopupOpened(true)
                }}
                allowShowInfo={allowSeeContacts.allow}
                onAllowShowInfoToggle={onAllowShowInfo}
                candidateDetails={candidateDetails}
                onApplicationActionsPopupOpen={() => setApplicationActionsPopupOpened(true)}
                onRequestResume={onRequestResume}
                onShortlistCandidate={onShortlistCandidate}
                onDeclineCandidate={onDeclineCandidate}
                onViewResume={onViewResume}
            />
            <Popup opened={resumePurchaseFailPopupOpened} onClose={onCloseResumePurchaseFailPopup}>
                <ResumePurchaseFail
                    onConfirm={onConfirmResumePurchaseFailPopup}
                    onCancel={onCloseResumePurchaseFailPopup}
                />
            </Popup>
            <Popup opened={noShowPopupOpened} onClose={onCloseNoShowPopup}>
                <NoShowConfirmation
                    onCancel={onCloseNoShowPopup}
                    onConfirm={onConfirmNoShowPopup}
                />
            </Popup>
            <Popup opened={showContactsConfirmationPopupOpened} onClose={onCloseShowContactsConfirmationPopup}>
                <ShowContactsConfirmation name={application.name} onConfirm={onCloseShowContactsConfirmationPopup}/>
            </Popup>
            {tokensPurchaseOpened ? (
                <PurchaseTokensFlow
                    onClose={onCloseTokensPurchasePopup}
                    onSuccess={onSuccessTokensPurchasePopup}
                />
            ) : null}
            <Popup opened={applicationActionsPopupOpened} onClose={onApplicationActionsPopupClose}>
                <ApplicationActions
                    onClose={onApplicationActionsPopupClose}
                    onSetInterviewStatus={onSetInterviewStatus}
                    onNoShowButtonClick={onNoShowButtonClick}
                />
            </Popup>
            <Popup opened={requestResumePopupOpened} onClose={onCloseRequestResumePopup}>
                <RequestResumeAction
                    onConfirm={onConfirmResumeActionPopup}
                    onCancel={onCloseRequestResumePopup}
                    freePeriod={job.freePeriod}   
                    isRecentlyViewed={recentlyViewedResume === 1}
                />
            </Popup>
            <Popup opened={scoreInfoPopupOpened} onClose={onCloseScoreInfoPopup}>
                <CandidateScoreInfo
                    onConfirm={onCloseScoreInfoPopup}
                    onLink={onLinkCandidateScoreInfoPopup}
                />
            </Popup>
            <Popup opened={showContactsPopupOpened} onClose={onCloseShowContactsPopup}>
                <ShowContactsAction
                    onConfirm={onConfirmShowContactsPopup}
                    onCancel={onCloseShowContactsPopup}
                />
            </Popup>
            <FixedLoader
                loading={candidateStatsLoading || purchaseLoading || applicationLoading || allowSeeContactsLoading || recentlyViewedResponseLoading || getAllApplicationsLoading}
            />
            <FixedError error={candidateStatsError || applicationError || allowSeeContactsError || recentlyViewedResponseError || getAllApplicationsError}/>
        </PageWrapper>
    )
}
